
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Bell, Percent } from 'lucide-react';
import { supabase } from "@/integrations/supabase/client";

interface Announcement {
  title: string;
  content: string;
  active: boolean;
  imageUrl?: string;
  videoUrl?: string;
  discountActive?: boolean;
  discountPercentage?: string;
  discountDescription?: string;
  discountEndDate?: string;
}

export const AnnouncementPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const subscriptionRef = useRef<any>(null);

  useEffect(() => {
    const checkAnnouncements = async () => {
      try {
        const { data: settings, error } = await supabase
          .from('settings')
          .select('*')
          .in('key', [
            'announcement_title', 
            'announcement_content', 
            'announcement_active',
            'announcement_image_url',
            'announcement_video_url',
            'discount_active',
            'discount_percentage',
            'discount_description',
            'discount_end_date'
          ]);

        if (error) {
          console.error('Error fetching announcement settings:', error);
          return;
        }

        const settingsMap = settings.reduce((acc, setting) => {
          acc[setting.key] = setting.value;
          return acc;
        }, {} as Record<string, string>);

        const title = settingsMap['announcement_title'] || '';
        const content = settingsMap['announcement_content'] || '';
        const active = settingsMap['announcement_active'] === 'true';
        const imageUrl = settingsMap['announcement_image_url'] || '';
        const videoUrl = settingsMap['announcement_video_url'] || '';
        const discountActive = settingsMap['discount_active'] === 'true';
        const discountPercentage = settingsMap['discount_percentage'] || '0';
        const discountDescription = settingsMap['discount_description'] || '';
        const discountEndDate = settingsMap['discount_end_date'] || '';

        if (active && title && content) {
          const lastSeen = localStorage.getItem('announcementLastSeen');
          const today = new Date().toDateString();
          
          if (lastSeen !== today) {
            setAnnouncement({ 
              title, 
              content, 
              active,
              imageUrl,
              videoUrl,
              discountActive,
              discountPercentage,
              discountDescription,
              discountEndDate
            });
            setIsOpen(true);
          }
        }
      } catch (error) {
        console.error('Error checking announcements:', error);
      }
    };

    const timer = setTimeout(checkAnnouncements, 2000);

    // Cleanup existing subscription
    if (subscriptionRef.current) {
      supabase.removeChannel(subscriptionRef.current);
      subscriptionRef.current = null;
    }

    // Create unique channel name
    const channelName = `announcement-${Date.now()}-${Math.random().toString(36).substring(7)}`;

    const channel = supabase
      .channel(channelName)
      .on('postgres_changes', { 
        event: 'UPDATE', 
        schema: 'public', 
        table: 'settings',
        filter: 'key=in.(announcement_title,announcement_content,announcement_active,announcement_image_url,announcement_video_url,discount_active,discount_percentage,discount_description,discount_end_date)'
      }, (payload) => {
        console.log('Announcement realtime update:', payload);
        checkAnnouncements();
      })
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR') {
          console.warn('Announcement realtime subscription failed - continuing without realtime updates');
        }
      });

    subscriptionRef.current = channel;

    return () => {
      clearTimeout(timer);
      if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
        subscriptionRef.current = null;
      }
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
    const today = new Date().toDateString();
    localStorage.setItem('announcementLastSeen', today);
  };

  if (!announcement) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] bg-gray-800 border-2 border-green-400/50 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-green-300 font-mono flex items-center gap-2">
            <Bell className="h-6 w-6 text-green-400" />
            {announcement.title}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Main Content */}
          <div className="text-green-200 font-mono leading-relaxed">
            {announcement.content}
          </div>
          
          {/* Media Content */}
          {(announcement.imageUrl || announcement.videoUrl) && (
            <div className="space-y-3">
              {announcement.imageUrl && (
                <div className="rounded-lg overflow-hidden border border-green-400/30">
                  <img 
                    src={announcement.imageUrl} 
                    alt="Announcement" 
                    className="w-full h-auto"
                  />
                </div>
              )}
              
              {announcement.videoUrl && (
                <div className="rounded-lg overflow-hidden border border-green-400/30">
                  <video 
                    src={announcement.videoUrl} 
                    controls 
                    className="w-full h-auto"
                  />
                </div>
              )}
            </div>
          )}

          {/* Discount Information */}
          {announcement.discountActive && parseInt(announcement.discountPercentage || '0') > 0 && (
            <div className="p-4 bg-green-500/10 rounded-lg border border-green-400/30">
              <div className="flex items-center gap-2 mb-2">
                <Percent className="h-5 w-5 text-green-400" />
                <h3 className="text-lg font-bold text-green-300 font-mono">
                  Հատուկ զեղչ՝ {announcement.discountPercentage}%
                </h3>
              </div>
              
              {announcement.discountDescription && (
                <p className="text-green-200 font-mono text-sm mb-2">
                  {announcement.discountDescription}
                </p>
              )}
              
              {announcement.discountEndDate && (
                <p className="text-green-200 font-mono text-xs">
                  Վավեր է մինչև՝ {new Date(announcement.discountEndDate).toLocaleString('hy-AM')}
                </p>
              )}
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <Button 
              onClick={handleClose}
              className="bg-green-600 hover:bg-green-700 font-mono"
            >
              Հասկացա
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
